import React, { useState } from "react";
import { X, Plus, Trash2, Upload } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { CATEGORIES } from "@/data/constants";

const label = "font-sans text-[11px] tracking-luxe uppercase text-softgrey/70";
const inputCls = "w-full border border-gold/20 bg-obsidian px-3 py-2 font-sans text-sm text-warmwhite outline-none focus:border-gold";

const EMPTY = {
  title: "", category: "Property & Real Estate", deal_id: "", country: "", location: "", asset_type: "",
  positioning: "", starting_price: "", key_metric: "", founders_fee: "", fee_type: "Fixed",
  badge: "Confidential", confidential: true, value_proposition: "",
  overview: [], location_advantage: [], snapshot_from: "", snapshot_items: [], payment_structure: [],
  investor_highlights: [], amenities: [], disclaimer: "",
  metrics: { show: true, projected_yield: "", capital_growth: "", target_rental: "", investor_strategies: "", entry_position: "" },
  images: [], documents: [], featured: true, status: "active", order: 0,
};

function ListEditor({ title, value, onChange }) {
  return (
    <div>
      <div className={label}>{title}</div>
      <div className="mt-2 space-y-2">
        {(value || []).map((v, i) => (
          <div key={i} className="flex gap-2">
            <input value={v} onChange={(e) => { const n = [...value]; n[i] = e.target.value; onChange(n); }} className={inputCls} />
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="border border-gold/20 px-2 text-softgrey hover:text-red-400"><Trash2 size={14} /></button>
          </div>
        ))}
        <button onClick={() => onChange([...(value || []), ""])} className="flex items-center gap-1 font-sans text-xs text-gold"><Plus size={13} /> Add</button>
      </div>
    </div>
  );
}

export default function OpportunityForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial ? { ...EMPTY, ...initial } : EMPTY);
  const [cats, setCats] = useState(CATEGORIES);
  const [imgUrl, setImgUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setM = (k, v) => setF((p) => ({ ...p, metrics: { ...p.metrics, [k]: v } }));

  React.useEffect(() => {
    api.get("/categories").then((r) => { if (r.data.length) setCats(r.data.map((c) => c.name)); }).catch(() => {});
  }, []);

  const uploadImg = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setF((p) => ({ ...p, images: [...p.images, data.url] }));
      }
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!f.title) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/opportunities/${initial.id}`, f);
      else await api.post("/opportunities", f);
      toast.success("Opportunity saved");
      onSaved();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end bg-obsidian/70 backdrop-blur-sm" data-testid="opportunity-form">
      <div className="h-full w-full max-w-2xl overflow-y-auto border-l border-gold/20 bg-obsidian p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl text-warmwhite">{initial?.id ? "Edit" : "New"} Opportunity</h2>
          <button onClick={onClose} className="text-softgrey hover:text-warmwhite"><X size={22} /></button>
        </div>

        <div className="mt-8 space-y-6">
          <div><div className={label}>Title</div><input value={f.title} onChange={(e) => set("title", e.target.value)} className={`${inputCls} mt-2`} data-testid="opp-title" /></div>

          <div className="grid grid-cols-2 gap-4">
            <div><div className={label}>Category</div>
              <select value={f.category} onChange={(e) => set("category", e.target.value)} className={`${inputCls} mt-2 [&>option]:bg-obsidian`}>
                {cats.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><div className={label}>Deal ID (auto if blank)</div><input value={f.deal_id} onChange={(e) => set("deal_id", e.target.value)} className={`${inputCls} mt-2`} /></div>
            <div><div className={label}>Country</div><input value={f.country} onChange={(e) => set("country", e.target.value)} className={`${inputCls} mt-2`} /></div>
            <div><div className={label}>Broad Location</div><input value={f.location} onChange={(e) => set("location", e.target.value)} className={`${inputCls} mt-2`} /></div>
            <div><div className={label}>Asset / Deal Type</div><input value={f.asset_type} onChange={(e) => set("asset_type", e.target.value)} className={`${inputCls} mt-2`} /></div>
            <div><div className={label}>Positioning</div><input value={f.positioning} onChange={(e) => set("positioning", e.target.value)} className={`${inputCls} mt-2`} /></div>
            <div><div className={label}>Starting Price / Value</div><input value={f.starting_price} onChange={(e) => set("starting_price", e.target.value)} className={`${inputCls} mt-2`} /></div>
            <div><div className={label}>Key Commercial Metric</div><input value={f.key_metric} onChange={(e) => set("key_metric", e.target.value)} className={`${inputCls} mt-2`} /></div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><div className={label}>Sourcing / Founder's Fee</div><input value={f.founders_fee} onChange={(e) => set("founders_fee", e.target.value)} className={`${inputCls} mt-2`} /></div>
            <div><div className={label}>Fee Structure</div>
              <select value={f.fee_type} onChange={(e) => set("fee_type", e.target.value)} className={`${inputCls} mt-2 [&>option]:bg-obsidian`}>
                {["Fixed", "Percentage", "Retainer", "Success", "Combination"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div><div className={label}>Badge</div>
              <select value={f.badge} onChange={(e) => set("badge", e.target.value)} className={`${inputCls} mt-2 [&>option]:bg-obsidian`}>
                {["Confidential", "Exclusive", "Off-Market"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div><div className={label}>Status</div>
              <select value={f.status} onChange={(e) => set("status", e.target.value)} className={`${inputCls} mt-2 [&>option]:bg-obsidian`}>
                {["active", "archived"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 font-sans text-sm text-softgrey"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured on homepage</label>
            <label className="flex items-center gap-2 font-sans text-sm text-softgrey"><input type="checkbox" checked={f.confidential} onChange={(e) => set("confidential", e.target.checked)} /> Confidential</label>
          </div>

          <div><div className={label}>Value Proposition</div><textarea rows={2} value={f.value_proposition} onChange={(e) => set("value_proposition", e.target.value)} className={`${inputCls} mt-2`} /></div>

          {/* Metrics */}
          <div className="border border-gold/15 p-5">
            <label className="flex items-center gap-2 font-sans text-sm text-gold"><input type="checkbox" checked={f.metrics.show} onChange={(e) => setM("show", e.target.checked)} /> Show Investor Metrics Panel</label>
            <div className="mt-4 grid grid-cols-2 gap-4">
              {[["projected_yield", "Projected Yield"], ["capital_growth", "Est. Capital Growth"], ["target_rental", "Target Rental"], ["investor_strategies", "Investor Strategies"], ["entry_position", "Entry / Payment Position"]].map(([k, l]) => (
                <div key={k}><div className={label}>{l}</div><input value={f.metrics[k]} onChange={(e) => setM(k, e.target.value)} className={`${inputCls} mt-2`} /></div>
              ))}
            </div>
          </div>

          <div><div className={label}>Snapshot Starting From</div><input value={f.snapshot_from} onChange={(e) => set("snapshot_from", e.target.value)} className={`${inputCls} mt-2`} /></div>
          <ListEditor title="Overview" value={f.overview} onChange={(v) => set("overview", v)} />
          <ListEditor title="Location Advantage" value={f.location_advantage} onChange={(v) => set("location_advantage", v)} />
          <ListEditor title="Snapshot Items" value={f.snapshot_items} onChange={(v) => set("snapshot_items", v)} />
          <ListEditor title="Payment Structure" value={f.payment_structure} onChange={(v) => set("payment_structure", v)} />
          <ListEditor title="Investor Highlights" value={f.investor_highlights} onChange={(v) => set("investor_highlights", v)} />
          <ListEditor title="Amenities" value={f.amenities} onChange={(v) => set("amenities", v)} />
          <div><div className={label}>Disclaimer</div><textarea rows={3} value={f.disclaimer} onChange={(e) => set("disclaimer", e.target.value)} className={`${inputCls} mt-2`} /></div>

          {/* Images */}
          <div>
            <div className={label}>Images</div>
            <div className="mt-2 flex flex-wrap gap-3">
              {f.images.map((img, i) => (
                <div key={i} className="relative h-20 w-28 border border-gold/20">
                  <img src={fileUrl(img)} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => set("images", f.images.filter((_, j) => j !== i))} className="absolute right-0 top-0 bg-obsidian/80 p-1 text-red-400"><X size={12} /></button>
                </div>
              ))}
              <label className="flex h-20 w-28 cursor-pointer items-center justify-center border border-dashed border-gold/30 text-gold">
                <Upload size={16} /> <input type="file" multiple accept="image/*" className="hidden" onChange={uploadImg} />
              </label>
            </div>
            <div className="mt-3 flex gap-2">
              <input value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="Or paste image URL (e.g. Cloudinary)" className={inputCls} data-testid="opp-image-url" />
              <button type="button" onClick={() => { if (imgUrl.trim()) { set("images", [...f.images, imgUrl.trim()]); setImgUrl(""); } }} className="flex items-center gap-1 bg-gold px-4 font-sans text-sm font-medium text-obsidian" data-testid="opp-image-url-add"><Plus size={14} /> Add</button>
            </div>
            {uploading && <p className="mt-2 font-sans text-xs text-gold">Uploading…</p>}
          </div>
        </div>

        <div className="mt-10 flex gap-3">
          <button onClick={save} disabled={saving} className="flex-1 bg-gold py-3 font-sans text-sm font-medium text-obsidian hover:brightness-110 disabled:opacity-60" data-testid="opp-save">
            {saving ? "Saving…" : "Save Opportunity"}
          </button>
          <button onClick={onClose} className="border border-gold/30 px-6 font-sans text-sm text-warmwhite hover:bg-gold/10">Cancel</button>
        </div>
      </div>
    </div>
  );
}
